/**
 * Разбор списка прокси для чекера (как client-frontend parseProxyCheckerDraft),
 * плюс формат админки: protocol://host:port:login:password
 */

const normalizeProtocol = (raw) => {
  if (!raw) {
    return 'HTTP';
  }
  const u = raw.toLowerCase().replace(/:$/, '');
  if (u === 'socks' || u === 'socks4' || u === 'socks5') {
    return 'SOCKS5';
  }
  if (u === 'https') {
    return 'HTTPS';
  }
  return raw.toUpperCase();
};

const isValidPort = (p) => {
  const n = Number(p);
  return Number.isInteger(n) && n >= 1 && n <= 65535;
};

const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

/**
 * Сегмент, похожий на хост (IPv4, домен, localhost, одна метка без двоеточий).
 * Не считаем хостом строку из одних цифр длиной ≥2 (чтобы не путать с портом).
 */
const isPlausibleHostSegment = (h) => {
  if (h == null || h === '') {
    return false;
  }
  const t = String(h).trim();
  if (t.length > 253) {
    return false;
  }
  if (IPV4_RE.test(t)) {
    return true;
  }
  if (t === 'localhost') {
    return true;
  }
  if (/[\s/:]/.test(t)) {
    return false;
  }
  if (/^\d+$/.test(t) && t.length >= 2) {
    return false;
  }
  if (/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(t)) {
    return true;
  }
  if (
    /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/i.test(
      t
    )
  ) {
    return true;
  }
  return false;
};

/** Чем выше — тем увереннее, что сегмент именно хост (снижает ложные совпадения с login/password). */
const hostSegmentConfidence = (host) => {
  const h = String(host);
  let q = 0;
  if (IPV4_RE.test(h)) {
    q += 300;
  } else if (h === 'localhost') {
    q += 200;
  } else if (h.includes('.')) {
    q += 150;
  } else {
    q += Math.min(h.length, 32);
  }
  return q;
};

/** Типовой порядок раскладок (pi = индекс порта, hi = индекс хоста) — запасной tie-break. */
const FOUR_COLON_PATTERN_PRIORITY = [
  [1, 0],
  [3, 2],
  [2, 1],
  [3, 0],
  [0, 1],
  [1, 2],
  [2, 3],
  [0, 2],
  [0, 3],
  [1, 3],
  [2, 0],
  [3, 1],
];

const fourColonPatternOrder = (pi, hi) => {
  const idx = FOUR_COLON_PATTERN_PRIORITY.findIndex(([p, h]) => p === pi && h === hi);
  return idx >= 0 ? idx : 100;
};

/** Четыре поля через «:» без @ и без схемы — host, port, login, password в разном порядке. */
const tryParseFourColonFlexibleOrder = (chunk) => {
  const t = chunk.trim();
  if (t.includes('@') || t.includes('://') || t.includes('[')) {
    return null;
  }
  const parts = t.split(':');
  if (parts.length !== 4) {
    return null;
  }

  const candidates = [];
  for (let pi = 0; pi < 4; pi += 1) {
    if (!isValidPort(parts[pi])) {
      continue;
    }
    for (let hi = 0; hi < 4; hi += 1) {
      if (hi === pi) {
        continue;
      }
      if (!isPlausibleHostSegment(parts[hi])) {
        continue;
      }
      const restIdx = [0, 1, 2, 3].filter((i) => i !== pi && i !== hi).sort((a, b) => a - b);
      candidates.push({
        hq: hostSegmentConfidence(parts[hi]),
        po: fourColonPatternOrder(pi, hi),
        host: parts[hi],
        port: String(Number(parts[pi])),
        username: parts[restIdx[0]],
        password: parts[restIdx[1]],
      });
    }
  }
  if (candidates.length === 0) {
    return null;
  }
  candidates.sort((a, b) => {
    if (b.hq !== a.hq) {
      return b.hq - a.hq;
    }
    return a.po - b.po;
  });
  const best = candidates[0];
  return {
    host: best.host,
    port: best.port,
    proxyType: 'HTTP',
    username: best.username,
    password: best.password,
  };
};

const parseHostPortSegment = (hostPort) => {
  const trimmed = hostPort.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith('[') && trimmed.includes(']:')) {
    const end = trimmed.indexOf(']:');
    if (end === -1) {
      return null;
    }
    const host = trimmed.slice(1, end);
    const port = trimmed.slice(end + 2);
    if (!host || !isValidPort(port)) {
      return null;
    }
    return { host, port };
  }
  const lio = trimmed.lastIndexOf(':');
  if (lio <= 0) {
    return null;
  }
  const host = trimmed.slice(0, lio).trim();
  const port = trimmed.slice(lio + 1).trim();
  if (!host || !isValidPort(port)) {
    return null;
  }
  return { host, port };
};

const splitUserPassword = (authPart) => {
  const colon = authPart.indexOf(':');
  if (colon < 0) {
    return { username: authPart };
  }
  return {
    username: authPart.slice(0, colon),
    password: authPart.slice(colon + 1),
  };
};

/** Классика: login:password @ host:port */
const tryParseCredentialsAtHostPort = (credsPart, hostPortPart, proxyType) => {
  const hp = parseHostPortSegment(hostPortPart);
  if (!hp || !String(credsPart).trim()) {
    return null;
  }
  const { username, password } = splitUserPassword(credsPart);
  return {
    ...hp,
    proxyType,
    username,
    password,
  };
};

/** Нестандартно: host:port @ login:password (в т.ч. scheme://… — когда URL() падает). */
const tryParseHostPortAtCredentials = (hostPortPart, credsPart, proxyType) => {
  const hp = parseHostPortSegment(hostPortPart);
  if (!hp || !String(credsPart).trim()) {
    return null;
  }
  const { username, password } = splitUserPassword(credsPart);
  return {
    ...hp,
    proxyType,
    username,
    password,
  };
};

/**
 * Часть после схемы или строка без схемы с @: сначала пробуем userinfo@host (последний @),
 * иначе host:port@credentials (первый @ — достаточно для одной «собаки»).
 */
const tryParseRestWithAt = (rest, proxyType) => {
  if (!rest.includes('@')) {
    return null;
  }
  const atLast = rest.lastIndexOf('@');
  if (atLast <= 0) {
    return null;
  }
  const std = tryParseCredentialsAtHostPort(
    rest.slice(0, atLast),
    rest.slice(atLast + 1),
    proxyType
  );
  if (std) {
    return std;
  }
  const atFirst = rest.indexOf('@');
  if (atFirst < 0) {
    return null;
  }
  return tryParseHostPortAtCredentials(
    rest.slice(0, atFirst),
    rest.slice(atFirst + 1),
    proxyType
  );
};

/** protocol://host:port:login:password (как checker_line из раздела «Прокси») */
const tryParseAdminColonLine = (chunk) => {
  const re = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/:]+):(\d+):(.+)$/;
  const m = re.exec(chunk.trim());
  if (!m) {
    return null;
  }
  const [, proto, host, port, tail] = m;
  if (!isValidPort(port)) {
    return null;
  }
  const si = tail.indexOf(':');
  const username = si >= 0 ? tail.slice(0, si) : tail;
  const password = si >= 0 ? tail.slice(si + 1) : '';
  return {
    host,
    port,
    proxyType: normalizeProtocol(proto),
    username,
    password,
  };
};

const parseOneProxyChunk = (s) => {
  const chunk = s.trim();
  if (!chunk || chunk.startsWith('#')) {
    return null;
  }

  const adminParsed = tryParseAdminColonLine(chunk);
  if (adminParsed) {
    return adminParsed;
  }

  if (chunk.includes('://')) {
    try {
      const u = new URL(chunk);
      if (!u.hostname) {
        return null;
      }
      let port = u.port;
      if (!port) {
        if (u.protocol === 'http:') {
          port = '80';
        } else if (u.protocol === 'https:') {
          port = '443';
        } else if (u.protocol === 'socks:' || u.protocol === 'socks4:' || u.protocol === 'socks5:') {
          port = '1080';
        } else {
          return null;
        }
      }
      if (!isValidPort(port)) {
        return null;
      }
      const userInfo =
        u.username || u.password ? { username: u.username || undefined, password: u.password || undefined } : {};
      return {
        host: u.hostname,
        port,
        proxyType: normalizeProtocol(u.protocol.replace(':', '')),
        ...userInfo,
      };
    } catch {
      /* fall through */
    }
    const idx = chunk.indexOf('://');
    const protocol = chunk.slice(0, idx).trim();
    const rest = chunk.slice(idx + 3);
    const protoNorm = normalizeProtocol(protocol);
    if (rest.includes('@')) {
      const parsedAt = tryParseRestWithAt(rest, protoNorm);
      if (parsedAt) {
        return parsedAt;
      }
      return null;
    }
    const hpBare = parseHostPortSegment(rest);
    if (!hpBare) {
      return null;
    }
    return { ...hpBare, proxyType: protoNorm };
  }

  if (chunk.includes('@')) {
    const parsed = tryParseRestWithAt(chunk, 'HTTP');
    if (parsed) {
      return parsed;
    }
    return null;
  }

  const quadParsed = tryParseFourColonFlexibleOrder(chunk);
  if (quadParsed) {
    return quadParsed;
  }

  const hp = parseHostPortSegment(chunk);
  if (!hp) {
    return null;
  }
  return { ...hp, proxyType: 'HTTP' };
};

/**
 * Разбор списка прокси из текста (вручную / файл / URL).
 * Поддержка: URL с userinfo (в т.ч. IPv6 в []), protocol://host:port без auth,
 * login:password@host:port, host:port@login:password, scheme://host:port@login:password,
 * host:port и [IPv6]:port, protocol://host:port:login:password,
 * четыре поля через «:» без схемы — host / port / login / password в разном порядке (см. tryParseFourColonFlexibleOrder),
 * несколько записей в строке через запятую; строки # — комментарии.
 */
export function parseProxyCheckerDraft(raw) {
  const out = [];
  const seen = new Set();

  for (const line of raw.split(/\r?\n/)) {
    const trimmedLine = line.trim();
    if (!trimmedLine) {
      continue;
    }
    const parts = trimmedLine.split(',').map((p) => p.trim()).filter(Boolean);
    const segments = parts.length > 0 ? parts : [trimmedLine];
    for (const seg of segments) {
      const parsed = parseOneProxyChunk(seg);
      if (!parsed) {
        continue;
      }
      const key = `${parsed.host.toLowerCase()}:${parsed.port}:${parsed.proxyType.toUpperCase()}:${parsed.username ?? ''}:${parsed.password ?? ''}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      out.push(parsed);
    }
  }

  return out;
}

export function buildAdminCheckerLineFromParsed(p) {
  const raw = String(p.proxyType || 'HTTP').toUpperCase();
  let proto = 'http';
  if (raw.includes('SOCK')) {
    proto = raw.includes('4') && !raw.includes('5') ? 'socks4' : 'socks5';
  } else if (raw === 'HTTPS') {
    proto = 'https';
  }
  const host = String(p.host || '').trim();
  const port = String(p.port || '').trim();
  const login = p.username != null ? String(p.username) : '';
  const password = p.password != null ? String(p.password) : '';
  return `${proto}://${host}:${port}:${login}:${password}`;
}
